import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import ContactsDialog from "@/components/ContactsDialog";
import { useSafePolling } from "@/lib/useSafePolling";

export default function MessagesPanel({ contactsEndpoint }) {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const scrollerRef = useRef(null);
  const lastMsgCountRef = useRef(0);
  const userScrolledUpRef = useRef(false);

  const loadConvs = async (signal) => {
    try {
      const { data } = await api.get("/conversations", { signal });
      setConvs(data || []);
      if (!active && data?.length) setActive(data[0]);
    } catch {}
  };

  const loadMessages = async (cid, signal) => {
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`, { signal });
      setMessages(data || []);
    } catch {}
  };

  const deleteConversation = async (e, conv) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this conversation with ${conv.other?.name || "this user"}? It will be removed from your inbox but they'll still see it.`)) return;
    try {
      await api.delete(`/conversations/${conv.conversation_id}`);
      toast.success("Conversation deleted");
      if (active?.conversation_id === conv.conversation_id) {
        setActive(null);
        setMessages([]);
      }
      loadConvs();
    } catch {
      toast.error("Couldn't delete conversation");
    }
  };

  // After messages change: scroll to bottom ONLY if there are new messages AND the user
  // hasn't manually scrolled up to read history. This kills the every-5s slide jitter.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const grew = messages.length > lastMsgCountRef.current;
    lastMsgCountRef.current = messages.length;
    if (grew && !userScrolledUpRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Detect when user scrolls up so we stop auto-scrolling them back down.
  const onScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    userScrolledUpRef.current = !nearBottom;
  };

  useSafePolling(loadConvs, 5000);

  useEffect(() => {
    if (active) {
      // Reset scroll tracking when switching conversations
      lastMsgCountRef.current = 0;
      userScrolledUpRef.current = false;
    }
  }, [active?.conversation_id]);

  useSafePolling((signal) => active ? loadMessages(active.conversation_id, signal) : Promise.resolve(), 5000, [active?.conversation_id]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !active) return;
    try {
      await api.post("/messages", { conversation_id: active.conversation_id, content: input.trim() });
      setInput("");
      userScrolledUpRef.current = false; // Sending a message implies user wants to be at the bottom
      loadMessages(active.conversation_id);
    } catch {}
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-0 h-full aamg-card overflow-hidden">
      <div className={`border-b md:border-b-0 md:border-r border-slate-200 flex flex-col min-h-0 ${active ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-slate-900 text-sm">Conversations</h3>
          {contactsEndpoint && (
            <ContactsDialog endpoint={contactsEndpoint} label="New" onStarted={() => loadConvs()} />
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {convs.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">No conversations yet</div>}
          <ul>
            {convs.map((c) => {
              const isActive = active?.conversation_id === c.conversation_id;
              const initials = (c.other?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <li key={c.conversation_id}
                  onClick={() => setActive(c)}
                  data-testid={`conversation-${c.conversation_id}`}
                  className={`group px-4 py-3 cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${isActive ? "bg-[#339999]/5" : ""}`}>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-[#E0A96D]/30 text-[#339999] text-xs font-semibold">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline">
                        <span className="font-medium text-sm text-slate-900 truncate">{c.other?.name || "Unknown"}</span>
                        {c.unread > 0 && <span className="text-[10px] bg-[#E0A96D] text-[#339999] font-bold rounded-full px-1.5 py-0.5">{c.unread}</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{c.last_message?.content || "—"}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => deleteConversation(e, c)}
                      aria-label="Delete conversation"
                      data-testid={`delete-conversation-${c.conversation_id}`}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 md:transition-opacity text-slate-400 hover:text-red-500 p-1 rounded touch-manipulation"
                      style={{ opacity: window.matchMedia?.("(hover: none)").matches ? 1 : undefined }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className={`flex-col min-h-0 ${active ? "flex" : "hidden md:flex"}`}>
        {active ? (
          <>
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 shrink-0">
              <button onClick={() => setActive(null)} className="md:hidden text-slate-500 hover:text-slate-900" data-testid="messages-back-btn">←</button>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{active.other?.name}</div>
                <div className="text-xs text-slate-500 capitalize">{active.other?.role}</div>
              </div>
            </div>
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              className="flex-1 overflow-y-auto p-4 bg-slate-50/50"
              data-testid="messages-scroller"
            >
              <div className="space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-[#339999] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                        {m.content}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-slate-400"}`}>
                          {m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2 shrink-0">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Write a message…" data-testid="messages-input" />
              <Button type="submit" className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="messages-send-btn"><Send className="h-4 w-4" /></Button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation</div>
        )}
      </div>
    </div>
  );
}
